import apiClient from './client';

// ─── Typen ──────────────────────────────────────────────────────────────────

/**
 * Globale Rolle: Betreiber der Installation oder gewöhnliches Konto.
 *
 * Was jemand IN einem Stammbaum darf, steht hier nicht – das regelt seine
 * Mitgliedschaft. 'viewer' ist die alte Bezeichnung; die Migration auf
 * Mandanten hat alle Konten auf 'user' gesetzt, der Wert kann in Altbeständen
 * aber noch auftauchen.
 */
export type GlobaleRolle = 'admin' | 'user' | 'viewer';

export interface TreeRoleAssignment {
  treeId: string;
  treeName: string;
  roleIds: string[];
  roleNames: string[];
}

export interface User {
  id: string;
  email: string;
  role: GlobaleRolle;
  mustChangePassword: boolean;
  isActive: boolean;
  totpEnabled: boolean;
  /**
   * Hinterlegte Passkeys. Sie sind ebenfalls ein zweiter Faktor – ein Konto
   * mit `totpEnabled: false` und einem Passkey ist NICHT ungeschützt.
   */
  passkeys: number;
  /** Ausnahme für die Zahl eigener Stammbäume. null = es gilt der Tarif. */
  maxTrees: number | null;
  /** Wie viele Stammbäume dieses Konto besitzt. */
  ownedTrees: number;
  /** Warum das Konto gesperrt ist – wird dem Betroffenen beim Anmelden gezeigt. */
  deactivatedReason: string | null;
  deactivatedAt: string | null;
  /** Tarif dieses Kontos. null = Vorgabe-Tarif der Installation. */
  planId: string | null;
  plan: { id: string; name: string; maxTrees: number; maxStorageBytes: number | null } | null;
  /** Rollenzuweisungen JE Stammbaum (Rechte gelten mandantenspezifisch). */
  treeRoles: TreeRoleAssignment[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserDto {
  email: string;
  password: string;
  role: GlobaleRolle;
}

export interface UpdateUserDto {
  email?: string;
  role?: GlobaleRolle;
  isActive?: boolean;
  /** Ausnahme für die Zahl eigener Stammbäume. null = es gilt der Tarif. */
  maxTrees?: number | null;
  /** Tarif. null setzt auf den Vorgabe-Tarif zurück. */
  planId?: string | null;
}

// ─── API-Funktionen ─────────────────────────────────────────────────────────

/** Alle Benutzer laden (Admin) */
export async function getUsers(): Promise<User[]> {
  const { data } = await apiClient.get<User[]>('/users');
  return data;
}

/** Neuen Benutzer erstellen (Admin) */
export async function createUser(dto: CreateUserDto): Promise<User> {
  const { data } = await apiClient.post<User>('/users', dto);
  return data;
}

/** Benutzer bearbeiten (Admin) */
export async function updateUser(id: string, dto: UpdateUserDto): Promise<User> {
  const { data } = await apiClient.patch<User>(`/users/${id}`, dto);
  return data;
}

/** Passwort zurücksetzen (Admin) */
export async function resetUserPassword(id: string, newPassword: string): Promise<User> {
  const { data } = await apiClient.post<User>(`/users/${id}/reset-password`, { newPassword });
  return data;
}

/**
 * Zweiten Faktor eines Kontos entfernen (Betrieb).
 *
 * Der Notausgang für „Telefon verloren, Ersatzcodes auch weg". Der Grund ist
 * Pflicht: er steht im Protokoll und in der E-Mail an den Betroffenen.
 */
export async function resetZweiterFaktor(id: string, grund: string): Promise<User> {
  const { data } = await apiClient.post<User>(`/users/${id}/2fa/reset`, { grund });
  return data;
}

/** Benutzer löschen (Admin) */
export async function deleteUser(id: string): Promise<void> {
  await apiClient.delete(`/users/${id}`);
}

/** Benutzer per E-Mail einladen (Admin) */
export async function inviteUser(email: string, role: string): Promise<User> {
  const { data } = await apiClient.post<User>('/users/invite', { email, role });
  return data;
}

/** Einladung erneut senden (Admin) */
export async function resendInvite(id: string): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>(`/users/${id}/resend-invite`);
  return data;
}
